<template>
  <form
      class="text-center flex flex-col items-center w-full pt-[20px]"
      @submit.prevent.capture="submit"
  >
    <div
        class="w-[280px] h-[160px] flex items-center justify-center rounded-[12px]"
    >
      <img v-if="icon" :src="icon" :alt="currentMethod"/>
    </div>
    <h2 class="mt-[10px] font-semibold leading-snug text-[24px]">
      {{ title }}
    </h2>
    <p v-if="currentMethod === TYPES_2FA_OTP.SMS || currentMethod === TYPES_2FA_OTP.CALL"
       class="text-[var(--text-4)] text-[16px] tracking-wide mt-[12px]">
      {{ t("2faModal | receiveCodeBySmsOrCall", {phone: userPhone}) }}
    </p>

    <base-button
        v-if="currentMethod === TYPES_2FA_OTP.GOOGLE"
        size="sm"
        @click="onPaste"
        class="mt-[30px] no-purple-hover-paste"
        link
    >
      {{ $t("2faModal | pasteCode") }}
    </base-button>

    <base-popover
        v-else-if="currentMethod === TYPES_2FA_OTP.PUSH"
        class="mt-[30px]"
        popover-type="tooltip"
        :content-style="{ border: 'none', padding: '25px' }"
        @mouseenter.self.once="onMouseOver"
    >
      <template #trigger>
        <span
            class="flex gap-[2px] text-[var(--text-3)] text-[14px] text font-[400]"
        >
          {{ t("2faModal | dontHaveApp") }}
          <span class="text-[var(--brand)]">
            {{ t("2faModal | download") }}
          </span>
          <img src="../shared/assets/icon_qr.svg" alt="qr_icon"/>
        </span>
      </template>
      <template #content>
        <div class="mb-[25px] text-[var(--text-1)] font-normal text-[16px]">
          {{ t("2faModal | scanWithCamera") }}
        </div>
        <img src="../shared/assets/onelink_qr.svg" alt="qr_icon"/>
      </template>
    </base-popover>

    <otp-input
        v-model="code"
        :error="isOtpHasError"
        class="mt-[28px]"
        :num-inputs="numberOfInputs"
        @change="onOtpChange"
        input-type="number"
        :separator="separator"
    />

    <div
        v-show="!!clientError"
        class="mt-[25px] text-[var(--error)] text-[14px]"
    >
      {{ clientError }}
    </div>

    <div
        :class="[' relative w-full', !!clientError ? 'mt-[36px]' : 'mt-[20px]']"
    >
      <transition
          enter-from-class="opacity-0"
          enter-active-class="transition duration-300"
          leave-to-class="opacity-0"
          leave-active-class="transition duration-300"
      >
        <error-block v-show="!!serverError" :text="serverError"/>
      </transition>
      <div class="mb-[32px]" v-if="isResendAvailable">
        <div v-if="!isFinished" class="text-[var(--text-4)] text-[14px]">
          {{ t("2faModal | sendAgainAfter", {date: formattedTime}) }}
        </div>
        <base-button
            v-else
            link
            size="sm"
            type="monochrome"
            class="items-start no-purple-hover"
            @click="onResend"
        >
          <base-loader v-show="isResendLoading" diameter="16" color="var(--brand)"/>
          <span>
            {{ t("2faModal | resendCode") }}
          </span>
        </base-button>
      </div>

      <div class="mb-[32px]" v-if="currentMethod === TYPES_2FA_OTP.PUSH">
        <div v-if="!isFinished" class="text-[var(--text-4)] text-[14px]">
          {{ t("2faModal | getPhoneNumberAfter") }} {{ formattedTime }}
        </div>
        <base-button
            v-else
            link
            size="sm"
            type="monochrome"
            class="items-start no-purple-hover"
            @click="onGetCodeByPhoneNumber"
        >
          <base-loader
              v-show="isResendLoading"
              diameter="16"
              color="var(--brand)"
          />
          {{ t("2faModal | getCodeByPhoneNumber") }}
        </base-button>
      </div>

      <div class="mb-[32px]" v-if="currentMethod === TYPES_2FA_OTP.CALL">
        <div v-if="!isFinished" class="text-[var(--text-4)] text-[14px]">
          {{ t("2faModal | orGetSmsAfter", {date: formattedTime}) }}
        </div>
        <base-button
            v-else
            link
            size="sm"
            type="monochrome"
            class="no-purple-hover"
            @click="onGetCodeByPhoneNumber"
        >
          {{ t("2faModal | getCodeFromSms") }}
        </base-button>
      </div>

      <base-button :disabled="isButtonDisabled" submit class="!w-full">
        <base-loader v-show="isLoading" color="var(--text-1)"/>
        {{ t("2faModal | submit") }}
      </base-button>
    </div>
  </form>
</template>

<script setup>
import {TYPES_2FA_OTP} from "../../common/const";
import {computed, onBeforeMount} from "vue";
import ErrorBlock from "../shared/ui/ErrorBlock/index.vue";
import OtpInput from "../shared/ui/OtpInput/index.vue";
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {use2FA} from "../shared/composables/use2FA";
import BaseLoader from '@/packages/BaseLoader/Spinner/Spinner.vue'
import {useTimer} from "../shared/composables/timer";
import {BasePopover} from "@emcd-shared/base-popover";
import AnalyticsService from "@emcd-shared/analytics-service";
import {
  ANALYTICS_BUTTON_NAME,
  ANALYTICS_EVENT_TYPE,
  ANALYTICS_EVENT_SUBSCREEN,
} from "../shared/utils/analytics";

import SMSIcon from "../shared/assets/sms.svg";
import CAllIcon from "../shared/assets/phone_call.svg";
import GoogleIcon from "../shared/assets/sms.svg";
import PushIcon from "../shared/assets/push.svg";

const TIMER_MAX = 15;

const props = defineProps({
  secretKey: {
    type: String,
    default: "",
  },
  nextMethod: {
    type: String,
    default: "",
  },
  resendOnOpen: {
    type: Boolean,
    default: false,
  },
  inputLength: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  flow: {
    type: String,
    default: "default",
  },
  phone: {
    type: String,
    default: "",
  },
  onValidate: {
    type: Function,
    default: null,
  },
});
const emit = defineEmits(["resolve"]);

const {t} = useI18n();
const store = useStore();
const userPhone = computed(() => props.phone || store.state.auth?.secure?.phone || '');
const {formattedTime, isFinished, restartTimer} = useTimer(TIMER_MAX);
const {
  code,
  isLoading,
  clientError,
  serverError,
  numberOfInputs,
  isResendLoading,
  onSubmit,
  onReset,
  resendSms,
  currentMethod,
  setDetails,
} = use2FA(props, emit);

const title = computed(() => {
  switch (currentMethod.value) {
    case TYPES_2FA_OTP.GENERAL:
      return t("2faModal | enterCodeFromSmsOrCall");
    case TYPES_2FA_OTP.SMS:
      return t("2faModal | enterCodeFromSms");
    case TYPES_2FA_OTP.CALL:
      return t("2faModal | enterCodeFromCall");
    case TYPES_2FA_OTP.GOOGLE:
      return t("2faModal | enterCodeFromGoogle");
    case TYPES_2FA_OTP.PUSH:
      return t("2faModal | enterCodeFromPush");
    default:
      return null;
  }
});
const isOtpHasError = computed(
    () => !!clientError.value || !!serverError.value
);
const separator = computed(() =>
    currentMethod.value === TYPES_2FA_OTP.CALL ? "--" : ""
);
const icon = computed(() => {
  switch (currentMethod.value) {
    case TYPES_2FA_OTP.SMS:
      return SMSIcon;
    case TYPES_2FA_OTP.CALL:
    case TYPES_2FA_OTP.GENERAL:
      return CAllIcon;
    case TYPES_2FA_OTP.GOOGLE:
      return SMSIcon;
    case TYPES_2FA_OTP.PUSH:
      return PushIcon;
    default:
      return null;
  }
});
const isButtonDisabled = computed(() => {
  return code.value.length !== numberOfInputs.value;
});
const isResendAvailable = computed(() =>
    [TYPES_2FA_OTP.SMS, TYPES_2FA_OTP.GENERAL].includes(currentMethod.value)
);

function onOtpChange() {
  onReset();
}

function submit() {
  trackButton(ANALYTICS_BUTTON_NAME.SUBMIT);
  onSubmit();
}

function trackButton(buttonName) {
  trackAction(ANALYTICS_EVENT_TYPE.CLICK, {
    button_name: buttonName,
  });
}

function trackAction(event_type, event_data) {
  AnalyticsService.track(event_type, {
    ...event_data,
    screen: "2fa",
    product: "",
  });
}

function onMouseOver() {
  trackButton(ANALYTICS_BUTTON_NAME.DOWNLOAD);
}

async function onGetCodeByPhoneNumber() {
  trackButton(ANALYTICS_BUTTON_NAME.GO_TO_SMS);
  await resendSms(props.secretKey, props.flow);
  restartTimer();
}

async function onResend(track = true) {
  if (track) {
    trackButton(ANALYTICS_BUTTON_NAME.RESEND);
  }
  await resendSms(props.secretKey, props.flow);
  restartTimer();
}

async function onPaste() {
  trackButton(ANALYTICS_BUTTON_NAME.PASTE);
  code.value = await navigator.clipboard.readText();
}

function onResendOnOpen() {
  if (currentMethod.value !== TYPES_2FA_OTP.GOOGLE && props.resendOnOpen) {
    onResend(false);
  }
}

onBeforeMount(() => {
  setDetails({
    next: props.nextMethod,
    method: props.type,
    number: props.inputLength,
  });

  trackAction(ANALYTICS_EVENT_TYPE.POPUP, {
    subscreen: ANALYTICS_EVENT_SUBSCREEN[currentMethod.value],
  });

  onResendOnOpen();
});
</script>

<style scoped>
.no-purple-hover {
  --base-button-text-color-hover: var(--brand-hover);
  --base-button-text-color-focus: var(--brand-hover);
  --base-button-text-color-active: var(--brand-hover);
}

.no-purple-hover-paste {
  --base-button-text-color-hover: var(--brand-hover);
  --base-button-text-color-focus: var(--brand-hover);
  --base-button-text-color-active: var(--brand-hover);
}
</style>
